import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Warning, Lightning, Timer, TrendUp, TrendDown } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

function StatCard({ label, value, hint, tone = "default" }) {
  const toneMap = {
    default: "bg-white border-border",
    warn: "bg-orange-50 border-orange-300",
    danger: "bg-red-50 border-red-300",
    ok: "bg-green-50 border-green-300",
  };
  return (
    <div className={`border p-4 ${toneMap[tone]}`}>
      <div className="eyebrow mb-1">{label}</div>
      <div className="font-display text-3xl font-black">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function ProjectReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [burndown, setBurndown] = useState(null);

  useEffect(() => {
    api.get(`/reports/projects/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/reports/projects/${id}/burndown`).then(r => setBurndown(r.data)).catch(() => {});
  }, [id]);

  if (error) return <div className="p-12 text-sm text-[#FF3B30]">{error}</div>;
  if (!data) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  const { project, summary, per_assignee, overdue_tasks } = data;
  const laggingCount = per_assignee.filter(r => r.is_lagging).length;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <Link to={`/projects/${id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#002FA7]" data-testid="back-to-kanban">
          <ArrowLeft size={14} weight="bold" /> Back to board
        </Link>
        <div className="mt-3">
          <div className="eyebrow mb-1">Manager report</div>
          <h1 className="font-display text-4xl font-black tracking-tight" data-testid="report-project-name">{project.name}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground flex-wrap">
            <span className="font-mono-mini uppercase">{project.status}</span>
            {project.end_date && <span>Due {project.end_date}</span>}
            {summary.days_remaining !== null && summary.days_remaining !== undefined && (
              <span className={`font-mono-mini ${summary.days_remaining < 0 ? "text-[#FF3B30]" : ""}`}>
                {summary.days_remaining < 0 ? `${Math.abs(summary.days_remaining)} days overdue` : `${summary.days_remaining} days remaining`}
              </span>
            )}
            <span className="font-mono-mini">{project.progress}% complete</span>
          </div>
        </div>
      </div>

      <div className="p-8 lg:p-12 space-y-8">
        {/* Burndown chart */}
        {burndown && burndown.series?.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Timeline</div>
                <h2 className="font-display text-2xl font-bold">Burndown</h2>
              </div>
              <div className="text-xs font-mono-mini text-muted-foreground">Total points: {burndown.total_points}</div>
            </div>
            <div className="border border-border p-4" data-testid="burndown-chart">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={burndown.series}>
                  <CartesianGrid stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                  <Tooltip contentStyle={{ border: '1px solid #0A0A0A', borderRadius: 0, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="ideal" stroke="#6B7280" strokeDasharray="4 4" dot={false} name="Ideal" />
                  <Line type="monotone" dataKey="remaining" stroke="#002FA7" strokeWidth={2} dot={false} name="Actual" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary">
          <StatCard label="Tasks" value={summary.tasks_total} hint={`${summary.tasks_done} done · ${summary.tasks_pending} open`} />
          <StatCard label="Points" value={`${summary.points_done}/${summary.points_total}`} hint={`${summary.points_pending} remaining`} />
          <StatCard
            label="Overdue"
            value={summary.tasks_overdue}
            tone={summary.tasks_overdue > 0 ? "danger" : "ok"}
            hint={summary.tasks_overdue > 0 ? "Needs attention" : "On track"}
          />
          <StatCard
            label="Lagging"
            value={laggingCount}
            tone={laggingCount > 0 ? "warn" : "ok"}
            hint={laggingCount > 0 ? "Assignees behind pace" : "Team on pace"}
          />
        </div>

        {/* Per-assignee */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Per assignee</div>
              <h2 className="font-display text-2xl font-bold">Velocity & lag</h2>
            </div>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="per-assignee-table">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Assignee</th>
                  <th className="px-4 py-3 eyebrow">Role</th>
                  <th className="px-4 py-3 eyebrow text-right">Done</th>
                  <th className="px-4 py-3 eyebrow text-right">Pending</th>
                  <th className="px-4 py-3 eyebrow text-right">Overdue</th>
                  <th className="px-4 py-3 eyebrow text-right">Velocity (30d)</th>
                  <th className="px-4 py-3 eyebrow text-right">ETA (weeks)</th>
                  <th className="px-4 py-3 eyebrow">Status</th>
                </tr>
              </thead>
              <tbody>
                {per_assignee.map(r => (
                  <tr key={r.assignee_id} className="border-t border-border" data-testid={`assignee-row-${r.assignee_id}`}>
                    <td className="px-4 py-3 font-semibold">{r.assignee_name}</td>
                    <td className="px-4 py-3 font-mono-mini uppercase text-xs text-muted-foreground">{r.role || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-mini">{r.done_points} pt <span className="text-muted-foreground">({r.done_count})</span></td>
                    <td className="px-4 py-3 text-right font-mono-mini">{r.pending_points} pt <span className="text-muted-foreground">({r.pending_count})</span></td>
                    <td className="px-4 py-3 text-right font-mono-mini">
                      {r.overdue_count > 0 ? (
                        <span className="text-[#FF3B30] inline-flex items-center gap-1"><Warning size={12} weight="bold" /> {r.overdue_count}</span>
                      ) : "0"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-mini">
                      {r.velocity_30d} pt
                      {r.velocity_30d > 0 ? <TrendUp size={12} weight="bold" className="inline ml-1 text-[#22C55E]" /> : <TrendDown size={12} weight="bold" className="inline ml-1 text-muted-foreground" />}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-mini">{r.weeks_to_finish}</td>
                    <td className="px-4 py-3">
                      {r.is_lagging ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini uppercase border border-[#FF3B30] bg-red-50 text-[#FF3B30] px-1.5 py-0.5">
                          <Lightning size={10} weight="bold" /> Lagging
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono-mini uppercase border border-[#22C55E] bg-green-50 text-[#22C55E] px-1.5 py-0.5">
                          On pace
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {per_assignee.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground text-xs">No task data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Overdue */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Overdue</div>
              <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                <Timer size={22} weight="bold" /> Overdue tasks
              </h2>
            </div>
            <div className="text-xs font-mono-mini text-muted-foreground">{overdue_tasks.length} items</div>
          </div>
          {overdue_tasks.length === 0 ? (
            <div className="border border-border p-8 text-center text-sm text-muted-foreground">
              No overdue tasks. 🎉
            </div>
          ) : (
            <div className="border border-border" data-testid="overdue-list">
              {overdue_tasks.map(t => (
                <Link key={t.id} to={`/projects/${id}?task=${t.id}`} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted" data-testid={`overdue-${t.id}`}>
                  <div className="col-span-5 min-w-0">
                    <div className="text-sm font-semibold truncate">{t.title}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground uppercase">{t.status}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    {t.assignee_name || <span className="text-muted-foreground">Unassigned</span>}
                  </div>
                  <div className="col-span-2 text-xs font-mono-mini uppercase">{t.priority}</div>
                  <div className="col-span-2 text-xs font-mono-mini text-[#FF3B30]">Due {t.due_date}</div>
                  <div className="col-span-1 text-right text-xs font-mono-mini">{t.points} pt</div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
